import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";
import PopoverMenu from "@/components/ui/Popeover";
import TextField from "@/components/ui/Textfield";
import { Settings, Trash, ArrowRightLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { BiPlus, BiSearch, BiTrash } from "react-icons/bi";
import { BsPlus } from "react-icons/bs";
import useTeamStore, { Team } from "./store/teamsStore";
import { useShallow } from "zustand/react/shallow";
import Dropdown from "@/components/ui/DropDown";
import { getColorByHash, toTitleCase } from "@/helper/utils";
import useGlobalStore from "@/store/globalStore";
import useAuthStore from "@/store/authStore";
import TeamTransferDialog from "./components/team-transfer-dialog";

export const ManageTeam: React.FC = () => {
  const { getTeams, teams, createTeam, updateTeam, deleteTeam, sortField } = useTeamStore(
    useShallow((state) => ({
      getTeams: state.getTeams,
      teams: state.teams,
      createTeam: state.createTeam,
      updateTeam: state.updateTeam,
      deleteTeam: state.deleteTeam,
      sortField: state.sortField,
    }))
  );

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    teamType: "",
  });

  const user_role = useAuthStore((state) => state.user_role);

  const [teamToDelete, setTeamToDelete] = useState(null);
  const [transferSourceTeam, setTransferSourceTeam] = useState<Team | null>(
    null
  );
  const showSuccess = useGlobalStore((s) => s.showSuccess);

  const itemsPerPage = 25;

  const filteredPeople = (teams ?? []).filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPeople = filteredPeople.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const goPrevious = () => setCurrentPage((page) => Math.max(page - 1, 1));
  const goNext = () => setCurrentPage((page) => Math.min(page + 1, totalPages));

  const handleOpenCreate = () => {
    setEditingTeam(null);
    setFormData({
      name: "",
      teamType: "",
    });
    setDialogOpen(true);
  };

  const setError = useGlobalStore((state) => state.setError);

  const handleOpenEdit = (team: {
    id: string;
    name: string;
    team_type: string;
  }) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      teamType: team.team_type,
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!teamToDelete) return;

    try {
      await deleteTeam(teamToDelete.id);
      showSuccess("Team deleted successfully");
    } catch (error: any) {
      setError(error.message);
    } finally {
      setTeamToDelete(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    try {
      if (editingTeam) {
        await updateTeam({
          id: editingTeam.id,
          name: formData.name,
          team_type: formData.teamType,
          media_agency_id: editingTeam.media_agency_id,
        });
        showSuccess("Team updated successfully");
      } else {
        await createTeam(formData.name, formData.teamType);
        showSuccess("Team created successfully");
      }
    } catch (error: any) {
      setError(error.message);
    }

    setDialogOpen(false);
    setEditingTeam(null);
    setFormData({ name: "", teamType: "" });
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    getTeams(newSort || undefined);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  useEffect(() => {
    getTeams();
  }, []);

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold mb-8">Manage Teams</h1>

        <div className="flex justify-between mb-6">
          <TextField
            type="text"
            leadingIcon={<BiSearch />}
            placeholder="Search by keyword, name, etc."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-96"
          />
          {user_role !== "Global Admin" && (
            <Button
              type="primary"
              trailingIcon={<BsPlus fontSize={25} />}
              onClick={handleOpenCreate}
            >
              Create New Team
            </Button>
          )}
        </div>

        <div className="grid grid-cols-12 border-b border-gray-300 pb-2 mb-2 text-sm text-gray-700 select-none">
          <div className="col-span-8 flex items-center cursor-pointer gap-1" onClick={() => handleSort('name')}>
            Name
            {<SortIcon field="name" />}
          </div>
          <div className="col-span-3 flex items-center cursor-pointer gap-1" onClick={() => handleSort('team_type')}>
            Team Type
            {<SortIcon field="team_type" />}
          </div>
          <div className="col-span-1"></div>
        </div>

        <div className="divide-y divide-gray-300">
          {currentPeople.map((person, index) => {
            return (
              <div key={index} className="grid grid-cols-12 py-3 text-sm">
                <div className="col-span-8 flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg ${getColorByHash(
                      person.name
                    )}`}
                  >
                    {person.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex flex-col">
                    <div className="font-semibold">{person.name}</div>
                    {person.media_agency?.name && (
                      <div className="text-sm text-gray-500">
                        {person.media_agency.name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-3 flex items-center gap-3">
                  <div>{toTitleCase(person.team_type)}</div>
                </div>
                <div className="flex col-span-1 justify-end">
                  <PopoverMenu
                    items={[
                      {
                        label: "Edit Team Info",
                        showDivider: true,
                        icon: <Settings height={18} width={18} />,
                        onClick: () => handleOpenEdit(person),
                      },
                      {
                        label: "Transfer Member",
                        showDivider: true,
                        icon: <ArrowRightLeft height={18} width={18} />,
                        onClick: () => setTransferSourceTeam(person),
                      },
                      {
                        label: "Delete Team",
                        showDivider: false,
                        icon: <Trash height={18} width={18} />,
                        onClick: () => setTeamToDelete(person),
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>

        {/* Shared Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-xl bg-white">
            <DialogHeader>
              <div className="font-semibold text-xl text-center">
                {editingTeam ? "Edit Team" : "Create New Team"}
              </div>
              <DialogDescription className="mt-5">
                <div>
                  <div className="font-semibold mb-1">Team Name</div>
                  <TextField
                    type="text"
                    placeholder="Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full"
                  />
                </div>
                <div className="mt-3">
                  <div className="font-semibold mb-1">Team Type</div>
                  <Dropdown
                    selected={formData.teamType}
                    setSelected={(newValue) =>
                      setFormData({
                        ...formData,
                        teamType: newValue.toString(),
                      })
                    }
                    options={[
                      { title: "Client", value: "client" },
                      { title: "Media", value: "media" },
                      { title: "Creative", value: "creative" },
                    ]}
                    className="w-full"
                  />
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex sm:justify-center mt-2">
              <Button
                type="primary"
                trailingIcon={<BiPlus fontSize={20} />}
                onClick={handleSubmit}
              >
                {editingTeam ? "Save Changes" : "Create Team"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!teamToDelete}
          onOpenChange={() => setTeamToDelete(null)}
        >
          <DialogContent className="sm:max-w-xl bg-white">
            <DialogHeader>
              <div className="font-semibold text-xl text-center">
                Delete Team
              </div>
              <DialogDescription className="mt-4 text-center text-gray-600">
                Are you sure you want to delete{" "}
                <span className="font-semibold">{teamToDelete?.name}</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-center gap-4 mt-6">
              <Button
                type="secondary"
                trailingIcon={<BiTrash fontSize={18} />}
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TeamTransferDialog
          open={!!transferSourceTeam}
          onOpenChange={(open) => {
            if (!open) setTransferSourceTeam(null);
          }}
          sourceTeam={transferSourceTeam}
        />
      </div>
    </Layout>
  );
};

export default ManageTeam;
